/* -----------------------------------------------------------------------------
 * design tokens
 *
 * The scales the style props resolve against (`mt='16'`, `textColor='purple'`,
 * `font='display.16.bold'`). Keys keep the names the screens already use.
 * -------------------------------------------------------------------------- */

export const breakpoints = {
  mobile: 0,
  tablet: 768,
  desktop: 1224,
  desktopXL: 1824
} as const

export const colors = {
  white: '#FFFFFF',
  black: '#000000',
  shade01: '#FAFAFD',
  shade01b: '#F4F7FA',
  shade02: '#EFF1F3',
  shade02b: '#EFF3F8',
  shade03: '#E6E9ED',
  shade03b: '#E4EBF3',
  shade04: '#B5BECA',
  shade04b: '#D5DBE2',
  shade05: '#8493A6',
  shade05b: '#9498BD',
  shade06: '#6B7D94',
  shade07: '#09274D',
  shade08: '#061B36',
  shade09: '#030C17',
  blue: '#2DB1EA',
  blueShade: '#D1D5FC',
  paleBlue: '#F7F8FD',
  paleBlue02: '#F1F2FE',
  lightBlue: '#80DAF7',
  darkBlue: '#5E83F5',
  green: '#15C2B8',
  paleGreen: '#08CAA7',
  darkGreen: '#15B04A',
  lightGreen: '#E0F9F3',
  lightGreen02: '#EFFAF8',
  lightGreen03: '#C3EEE6',
  mintGreen: '#F4FFFD',
  everestGreen: '#28667B',
  newPurple: '#363F89',
  newMutedPurple: '#6670BC',
  purple: '#3E4088',
  lightPurple: '#C763F7',
  mutedPurple: '#6976AE',
  violet: '#5162F4',
  paleViolet: '#E3E7F8',
  lightViolet: '#F3F4FF',
  red: '#FF4359',
  paleRed: '#EE4F58',
  yellow: '#FFC449',
  paleYellow: '#F2C85B',
  orange: '#FF8719',
  paleOrange: '#FC9F5D',
  lightOrange: '#FFF6E0',
  darkOrange: '#5F3838',
  coolGray: '#888BB6',
  error: '#D10033',
  paleError: '#F9EFEF',
  pink: '#EE727F',
  lightPink: '#FAEDEC',
  darkPink: '#5B2E3B',
  transparent: 'transparent',
  //new-colors
  blueBayoux: '#536882',
  walrusRed050: '#F6F5F6',
  walrusRed200: '#E3C2D2',
  walrusRed800: '#85353A',
  walrusRed900: '#642A2E',
  walrusBlue050: '#F1F2FE',
  walrusBlue100: '#D1D5FC',
  walrusBlue200: '#363F89',
  walrusBlue300: '#404CAB',
  walrusBlue300_80: '#404CABCC',
  walrusBlue500: '#5162F4',
  walrusBlue700: '#404CAB',
  walrusGreen500: '#15B04A',
  walrusNeutralSecondary: '#F9F9F9',
  walrusNeutral700: '#8897AA',
  // --- protools identity (clinical dashboard) ---
  // additive only; consumed by the professional app. `proInk`, `proAccent` and
  // `proTeal` intentionally mirror `purple`, `violet` and `green` so the new
  // semantic layer stays anchored to the existing brand values.
  // surfaces
  proCanvas: '#F6F7FB',
  proSurface: '#FFFFFF',
  proSurfaceAlt: '#FBFBFE',
  proSurfaceHover: '#FAFBFF',
  proNavHover: '#F4F5FB',
  proRowAlert: '#FFFCFC',
  // borders
  proBorder: '#E8E9F3',
  proBorderStrong: '#C9CDF0',
  proDivider: '#EDEEF7',
  proDividerSoft: '#F1F2F9',
  // text ramp
  proTextBody: '#4B5563',
  proTextMuted: '#6B7280',
  proTextFaint: '#9CA3AF',
  proTextDisabled: '#D7D9E8',
  // brand
  proInk: '#3E4088',
  proInkTint: '#EAEAF6',
  proAccent: '#5162F4',
  proAccentDeep: '#3A45B8',
  proAccentTint: '#EEEEFE',
  // tint pairs for badges and tags
  proTeal: '#15C2B8',
  proTealDeep: '#0C7A73',
  proTealTint: '#E4F8F6',
  proNavy: '#1C3A4A',
  proNavyTint: '#E9EFF3',
  proAqua: '#9BCCCE',
  proAquaDeep: '#2C6D72',
  proAquaTint: '#EDF6F7',
  proDanger: '#FF4459',
  proDangerDeep: '#B3222F',
  proDangerTint: '#FFECEE',
  proDangerBorder: '#FFD3D8',
  proNeutralTint: '#F1F2F7',
  proOverlay: 'rgba(28, 32, 72, 0.28)'
} as const

export const spaces = {
  '-64': -64,
  '-48': -48,
  '-40': -40,
  '-32': -32,
  '-24': -24,
  '-20': -20,
  '-16': -16,
  '-12': -12,
  '-08': -8,
  '-04': -4,
  '-02': -2,
  '00': 0,
  '02': 2,
  '04': 4,
  '08': 8,
  '12': 12,
  '16': 16,
  '20': 20,
  '24': 24,
  '32': 32,
  '40': 40,
  '48': 48,
  '64': 64,
  '98': 98,
  // --- protools identity ---
  // lets `ml`/`mt` push a flex child, which the numeric scale cannot express
  auto: 'auto'
} as const

export const radii = {
  none: 0,
  xsmall: 2,
  small: 4,
  medium: 6,
  large: 12,
  xlarge: 20,
  pill: 99,
  // --- protools identity ---
  proXs: 6,
  proSm: 8,
  proMd: 10,
  proLg: 12,
  proXl: 14,
  proPill: 999
} as const

export const fonts = {
  'text.10.medium': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 10,
    fontWeight: '500',
    lineHeight: 12
  },
  'text.10.bold': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 10,
    fontWeight: '700',
    lineHeight: 12
  },
  'text.11.bold': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 11,
    fontWeight: '700',
    lineHeight: 12
  },
  'text.12.medium': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 16
  },
  'text.12.semibold': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 12,
    fontWeight: '600',
    lineHeight: 16
  },
  'text.12.bold': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 16
  },
  'text.14.medium': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 20
  },
  'text.14.semibold': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 20
  },
  'text.14.bold': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20
  },
  'text.15.medium': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 15,
    fontWeight: '500',
    lineHeight: 20
  },
  'text.16.medium': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 24
  },
  'text.16.semibold': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 16,
    fontWeight: '600',
    lineHeight: 24
  },
  'text.16.bold': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 24
  },
  'display.10.bold': {
    fontFamily: `'GT Walsheim', sans-serif`,
    fontSize: 10,
    fontWeight: '700',
    lineHeight: 14
  },
  'display.12.bold': {
    fontFamily: `'GT Walsheim', sans-serif`,
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 16
  },
  'display.14.bold': {
    fontFamily: `'GT Walsheim', sans-serif`,
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 18
  },
  'display.16.bold': {
    fontFamily: `'GT Walsheim', sans-serif`,
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 24
  },
  'display.18.bold': {
    fontFamily: `'GT Walsheim', sans-serif`,
    fontSize: 18,
    fontWeight: '700',
    lineHeight: 24
  },
  'display.20.bold': {
    fontFamily: `'GT Walsheim', sans-serif`,
    fontSize: 20,
    fontWeight: '700',
    lineHeight: 24
  },
  'text.22.medium': {
    fontFamily: 'Inter, sans-serif',
    fontSize: 22,
    fontWeight: '500',
    lineHeight: 24
  },
  'display.24.bold': {
    fontFamily: `'GT Walsheim', sans-serif`,
    fontSize: 24,
    fontWeight: '700',
    lineHeight: 32
  },
  'display.32.bold': {
    fontFamily: `'GT Walsheim', sans-serif`,
    fontSize: 32,
    fontWeight: '700',
    lineHeight: 40
  },
  // --- protools identity (Poppins) ---
  'pro.display.31.bold': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 31,
    fontWeight: '700',
    letterSpacing: -0.7,
    lineHeight: 38
  },
  'pro.display.25.bold': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 25,
    fontWeight: '700',
    lineHeight: 26
  },
  'pro.display.23.bold': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 23,
    fontWeight: '700',
    letterSpacing: -0.4,
    lineHeight: 30
  },
  'pro.display.22.bold': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 22,
    fontWeight: '700',
    lineHeight: 24
  },
  'pro.title.20.semibold': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 20,
    fontWeight: '600',
    letterSpacing: -0.3,
    lineHeight: 26
  },
  'pro.text.16.semibold': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 16,
    fontWeight: '600',
    lineHeight: 22
  },
  'pro.text.15.semibold': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 15,
    fontWeight: '600',
    lineHeight: 20
  },
  'pro.text.14.semibold': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 18
  },
  'pro.text.14.regular': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20
  },
  'pro.text.13.medium': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 13,
    fontWeight: '500',
    lineHeight: 18
  },
  'pro.text.13.regular': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 13,
    fontWeight: '400',
    lineHeight: 19
  },
  'pro.text.12.semibold': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 12,
    fontWeight: '600',
    lineHeight: 16
  },
  'pro.text.12.regular': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 17
  },
  'pro.eyebrow.11': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 1,
    lineHeight: 14,
    textTransform: 'uppercase'
  },
  'pro.label.11': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 11,
    fontWeight: '600',
    letterSpacing: 0.6,
    lineHeight: 14,
    textTransform: 'uppercase'
  },
  'pro.label.10': {
    fontFamily: 'Poppins, sans-serif',
    fontSize: 10,
    fontWeight: '600',
    letterSpacing: 0.4,
    lineHeight: 13,
    textTransform: 'uppercase'
  }
} as const

export const shadows = {
  medium: {
    shadowColor: 'rgb(62, 64, 136)',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 1
  },
  large: {
    shadowColor: 'rgb(9, 39, 77)',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.16,
    shadowRadius: 14
  },
  panel: {
    shadowColor: 'rgb(36, 38, 95)',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.14,
    shadowRadius: 3
  },
  // --- protools identity ---
  proCard: {
    shadowColor: 'rgb(30, 32, 80)',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 2
  },
  proTable: {
    shadowColor: 'rgb(30, 32, 80)',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3
  },
  proLift: {
    shadowColor: 'rgb(30, 32, 80)',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.1,
    shadowRadius: 16
  },
  proDrawer: {
    shadowColor: 'rgb(30, 32, 80)',
    shadowOffset: { width: -12, height: 0 },
    shadowOpacity: 0.16,
    shadowRadius: 40
  },
  proToast: {
    shadowColor: 'rgb(30, 32, 80)',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.28,
    shadowRadius: 32
  }
}

const theme = {
  breakpoints,
  colors,
  spaces,
  radii,
  fonts,
  shadows
} as const

export default theme
